import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import type { PriceId } from '@iheartjane/types';
import { useCallback } from 'react';

import type { AlgoliaProduct } from '@jane/search/types';
import type {
  Cart,
  EmptyCart,
  MaterializedMenuProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/types';
import { calculateDiscount } from '@jane/shared/util';

import { useKioskUserSegmentIds } from '../../../data-access';
import { trackAddedProductToBag } from '../../../util';

type addProductOnSuccessProps = {
  callback?: () => void;
  cart?: Cart | EmptyCart;
  discountPercentage: number | null;
  discountPrice?: number;
  isInlineAd?: boolean;
  isTopper?: boolean;
  product: AlgoliaProduct | MaterializedMenuProduct;
  quantity?: number;
  quickAdd?: boolean;
  selectedWeight: PriceId;
  source: string[];
  specialInScope: StoreSpecial | V3StoreSpecial | undefined;
  store: Store;
  unitPrice?: number;
};

/**
 * onSuccess is passed to useCreateCart, useUpdateCartProduct, and useAddCartProduct.
 * However, only useCreateCart returns a new cart and happens once. From this new cart,
 * we only need the cart id which is not updated on initial render for the default cart
 * object.
 */
export const useAddProductOnSuccess = ({
  callback,
  cart,
  discountPercentage,
  discountPrice,
  product,
  quantity,
  selectedWeight,
  source,
  store,
  isInlineAd,
  isTopper,
  unitPrice,
  quickAdd = false,
  specialInScope,
}: addProductOnSuccessProps) => {
  const userSegments = useKioskUserSegmentIds();

  return useCallback(
    (newCart?: Cart) => {
      trackAddedProductToBag({
        cart: newCart ?? cart,
        source: source,
        productDetails: {
          product,
          productPriceId: selectedWeight,
          productSavingsAmount:
            product.special_amount && unitPrice && discountPrice
              ? calculateDiscount(unitPrice, discountPrice)
              : null,
          productSpecialId: product.special_id,
          productPercentageOff: product.special_amount
            ? discountPercentage
            : null,
          productQuantity: quantity ?? 1,
        },
        store: store,
        ...(newCart && { cartId: String(newCart.id) }),
        quickAdd,
        dmType: isTopper ? ['bag'] : isInlineAd ? ['in-line'] : null,
        specialInScope,
      });
      callback?.();
    },
    [
      cart,
      source,
      product,
      selectedWeight,
      unitPrice,
      discountPrice,
      discountPercentage,
      quantity,
      store,
      quickAdd,
      isTopper,
      isInlineAd,
      specialInScope,
      callback,
      userSegments,
    ]
  );
};
